/**
 * IntelligencePanel — SPEC-014 · Enhanced AI Intelligence + Plan 6 TTS
 *
 * Displays AI-generated intelligence output in a tabbed layout:
 *   Summary       — combined output from the legacy 'generate' action
 *   Meeting Minutes — formal minutes (action: 'meeting-minutes')
 *   Key Decisions   — decision table (action: 'key-decisions')
 *   Action Items    — structured items with priority (action: 'action-items-v2')
 *   Follow-up Email — professional email draft (action: 'followup-email')
 *
 * Only renders tabs that have content. Falls back to single-tab view when
 * only legacy `intelligence` is present.
 *
 * Plan 6: Each tab has a "Listen" button that calls POST /api/voice { action:'tts' }
 * and renders an AudioPlayer inline. Audio is cached per-tab via the store.
 */

import { useState } from 'react';
import { AudioPlayer } from './AudioPlayer';

interface IntelligencePanelProps {
  /** Legacy combined intelligence output (from 'generate' action) */
  intelligence: string | null;
  /** Per-type outputs from SPEC-014 enhanced actions */
  outputs: Record<string, string>;
  /** Optional: callback when user clicks Regenerate for a given type */
  onRegenerate?: (type: string) => Promise<void>;
  /** Whether a regenerate request is in-flight */
  isRegenerating?: boolean;
  // ── Plan 6: TTS ──────────────────────────────────────────────────────
  /** Per-tab cached audio (base64 WAV) — managed externally by the store */
  ttsAudioUrls?: Record<string, string>;
  /** Per-tab TTS loading state */
  ttsLoading?: Record<string, boolean>;
  /** Callback to request TTS for a tab — receives tabId + text to synthesise */
  onListenClick?: (tabId: string, text: string) => Promise<void>;
}

interface TabDef {
  id: string;
  label: string;
  icon: string;
}

const TAB_DEFS: TabDef[] = [
  { id: 'summary',               label: 'Summary',              icon: '📊' },
  { id: 'meeting-minutes',       label: 'Minutes',              icon: '📋' },
  { id: 'key-decisions',         label: 'Decisions',            icon: '⚖️' },
  { id: 'action-items-v2',       label: 'Action Items',         icon: '✅' },
  { id: 'followup-email',        label: 'Follow-up Email',      icon: '✉️' },
  { id: 'multilingual-summary',  label: 'Multilingual Summary', icon: '🌍' },
];

const REGENERABLE = ['meeting-minutes', 'key-decisions', 'action-items-v2', 'followup-email', 'multilingual-summary'];

export function IntelligencePanel({
  intelligence,
  outputs,
  onRegenerate,
  isRegenerating = false,
  ttsAudioUrls = {},
  ttsLoading = {},
  onListenClick,
}: IntelligencePanelProps) {
  // All hooks must be called unconditionally — before any early returns
  const [activeTab, setActiveTab] = useState<string>(() => {
    // Compute initial tab from whatever data is present at mount
    const firstAvailable = TAB_DEFS.find(t => {
      if (t.id === 'summary') return !!intelligence?.trim();
      return !!outputs[t.id]?.trim();
    });
    return firstAvailable?.id ?? 'summary';
  });

  // Build available tabs from present data
  const availableTabs = TAB_DEFS.filter(t => {
    if (t.id === 'summary') return !!intelligence?.trim();
    return !!outputs[t.id]?.trim();
  });

  if (availableTabs.length === 0) return null;

  // Make sure activeTab is still valid (could become stale if outputs change)
  const resolvedTab = availableTabs.find(t => t.id === activeTab)?.id ?? availableTabs[0].id;
  const currentContent = resolvedTab === 'summary'
    ? (intelligence ?? '')
    : (outputs[resolvedTab] ?? '');

  const tabAudio   = ttsAudioUrls[resolvedTab];
  const tabLoading = ttsLoading[resolvedTab] ?? false;

  return (
    <div className="mb-12 sm:mb-20 bg-[#00d4b8]/5 border border-[#00d4b8]/20 rounded-[32px] sm:rounded-[48px] border-l-[10px] sm:border-l-[14px] border-l-[#00d4b8] animate-in overflow-hidden">
      {/* Tab bar */}
      <div className="px-6 sm:px-12 pt-6 sm:pt-8 border-b border-[#00d4b8]/10 flex items-center gap-2 flex-wrap">
        {availableTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-all border
              ${resolvedTab === tab.id
                ? 'bg-[#00d4b8] text-white border-[#00d4b8] shadow-sm'
                : 'bg-white/60 text-slate-500 border-slate-200 hover:border-[#00d4b8]/50 hover:text-[#00d4b8]'
              }`}
            aria-pressed={resolvedTab === tab.id}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}

        {/* ── Plan 6: Listen button ─────────────────────────────────────── */}
        {onListenClick && currentContent && (
          <button
            onClick={() => onListenClick(resolvedTab, currentContent)}
            disabled={tabLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-all border bg-white text-slate-400 border-slate-200 hover:border-[#00d4b8]/50 hover:text-[#00d4b8] disabled:opacity-40 disabled:cursor-not-allowed"
            title={`Listen to ${availableTabs.find(t => t.id === resolvedTab)?.label}`}
          >
            {tabLoading
              ? <><span className="w-3 h-3 border border-slate-300 border-t-[#00d4b8] rounded-full animate-spin" /> Generating audio…</>
              : <><span>🔊</span> Listen</>
            }
          </button>
        )}

        {/* Regenerate button (for supported types when callback provided) */}
        {onRegenerate && REGENERABLE.includes(resolvedTab) && (
          <button
            onClick={() => onRegenerate(resolvedTab)}
            disabled={isRegenerating}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-all border bg-white text-slate-400 border-slate-200 hover:border-[#00d4b8]/50 hover:text-[#00d4b8] disabled:opacity-40 disabled:cursor-not-allowed"
            title={`Re-generate ${availableTabs.find(t => t.id === resolvedTab)?.label}`}
          >
            {isRegenerating
              ? <><span className="w-3 h-3 border border-slate-300 border-t-[#00d4b8] rounded-full animate-spin" /> Generating…</>
              : <><span>↺</span> Regenerate</>
            }
          </button>
        )}
      </div>

      {/* Tab label */}
      <div className="px-6 sm:px-12 pt-6 sm:pt-8 pb-0">
        <h4 className="text-[10px] sm:text-[11px] font-black text-[#00d4b8] uppercase tracking-[0.4em] sm:tracking-[0.6em] mb-4 sm:mb-6">
          {availableTabs.find(t => t.id === resolvedTab)?.icon}{' '}
          AI {availableTabs.find(t => t.id === resolvedTab)?.label}
        </h4>
      </div>

      {/* Content */}
      <div className="px-6 sm:px-12 pb-8 sm:pb-12">
        <div className="text-[13px] sm:text-[14px] leading-relaxed text-[#1a2332] font-normal whitespace-pre-wrap">
          {currentContent}
        </div>

        {/* ── Plan 6: Audio player (shown once TTS is ready) ───────────── */}
        {tabAudio && (
          <AudioPlayer audioBase64={tabAudio} />
        )}

        {/* EU AI Act Art. 50 transparency: visible AI-generated-content disclosure */}
        <p className="mt-4 text-[9px] sm:text-[10px] text-slate-400">AI-generated content</p>
      </div>
    </div>
  );
}
